import deepFreeze from 'deep-freeze'
import {
  COLUMN_TYPES,
  COLUMN_SORT_SETTING,
  COLUMN_STANDARD_SIZES,
} from './constants'

/**
 * Instead of having things like defaultColPropsWithWidth300 and defaultColPropsWithWidth100, we have an idea of
 * what a default column is, and it is mixed in here instead of each app copying and pasting objects just to set it. The
 * defaults always ensure properties we expect are always set to _some_ value.
 */
const defaultColumnProperties = {
  /**
   * Every key here is external, and the documentation ref can be found here
   * https://schrodinger.github.io/fixed-data-table-2/api-column.html
   *
   * We just define our own defaults to pass in to that library.
   */
  isResizable: true,
  isReorderable: true,

  /**
   * NOTE: Everything below this line is our own custom prop, and not from the core datatable component library we
   * rely on.
   */

  /**
   * Whether the column should render at all. Useful for fields product wants to search on, but does not care to see in
   * the table itself.
   */
  isVisible: false,

  /**
   * TODO: Why are the other switches prepended with is-* except this one? Did this one come before?
   * TODO: Can we alias this is isSearchable?
   */
  searchable: false,

  /**
   * Whether the column is one that can be included in a sort condition
   */
  sort: COLUMN_SORT_SETTING.NOT_SORTED,

  /**
   * If we do sort, if it ascending or descending, or to ignore any sort order specification
   *
   * TODO: rename this to be sortDirection which can support an enum
   */
  sortAscending: undefined,
}

/**
 * If a size is not explicitly given in the top level definitions, we can use our knowledge of different Cell format
 * types to approximate a 'best guess' based on data type. The motivation for this is to bake in knowledge about data
 * types into the code instead of copying around standard defaults from app to app.
 *
 * This works especially well for data where we know all possible input values. Take, for example, a Boolean type, where
 * we know the two values that will always be shown. In that case, we can be sure that the standard size we set looks
 * good for all possible inputs for that specific Cell.
 */
const calculateDefaultSizeForType = type =>
  ({
    [COLUMN_TYPES.CHECKBOX]: COLUMN_STANDARD_SIZES.XSMALL,

    [COLUMN_TYPES.BOOL]: COLUMN_STANDARD_SIZES.SMALL,
    [COLUMN_TYPES.LABEL]: COLUMN_STANDARD_SIZES.SMALL,
    [COLUMN_TYPES.STATUS]: COLUMN_STANDARD_SIZES.SMALL,
    [COLUMN_TYPES.NUMBER]: COLUMN_STANDARD_SIZES.SMALL,
    [COLUMN_TYPES.DATE]: COLUMN_STANDARD_SIZES.SMALL,
    [COLUMN_TYPES.VALUE_TEXT]: COLUMN_STANDARD_SIZES.SMALL,

    [COLUMN_TYPES.TEXT]: COLUMN_STANDARD_SIZES.LARGE,
    [COLUMN_TYPES.HTML]: COLUMN_STANDARD_SIZES.LARGE,
  }[type] || COLUMN_STANDARD_SIZES.MEDIUM)

const standardizeDefinitions = (columnDefinitions = []) =>
  columnDefinitions
    .slice(0)
    .map(columnDefinition => standardizeDefinition(columnDefinition))

const standardizeDefinition = fieldDef => {
  const columnDefinition = {
    /**
     * The original idea behind having colProps separated was that it's the part that the user can modify and the part
     * that fixed data table, the component we use for representing data, was concerned with.
     *
     * More explicitly: this is used to partition view props from other business logic props that may be added -- and
     * that we have had to add since the introduction of this component.
     *
     * Couple of reasons this is still holding up:
     * - We persist the current view column configuration into a cookie. That implementation because much easier if we
     * can count on just persisting the fields in the `colProps` object. And that is important, since we have size
     * limits for cookies that we need to keep in mind what to serialize, which is more of a problem for data tables
     * with lots of columns.
     * - As mentioned above, we now deal with cases where we muct specify configuration that does not directly affect
     * the view, but which we still want to define in a declarative, table driven way via this column schema definition.
     * Up to now, we have been able to do that without risking breaking something about how things are viewed, because
     * they are kept separate.
     */
    colProps: {
      ...defaultColumnProperties,
      width: calculateDefaultSizeForType(fieldDef.columnDefinition.type),
      ...fieldDef.columnDefinition.colProps,
    },
  }

  return {
    ...fieldDef,
    columnDefinition,
  }
}

/**
 * This ensures consistent naming for the same objects in all of our apps, and it centralizes what the column
 * definition actually means, so we are always following the same practices with regards to structure. By doing this, we
 * can implement additions in the toolkit and add potential validations on the table config so that we can always be
 * sure configuration in applications is as correct as it can possible be. It makes it more difficult for configuration
 * drift over time.
 *
 * We also want greater visibility into the default configuration of each column, perhaps we have a default setting for
 * column that we merge, so that every key that we deal with has a known default value instead of having to worry about
 * the existence of that key before reading it. To put it another way, instead of relying of the value of `column.key`
 * being undefined by way of it not existing at all, we assume it always exists, and that it has a default boolean
 * setting, for example.
 *
 * TODO: We only care about field definitions, for now. If we standardize even more, this serves as an entrypoint,
 * a beach head, if you will, from which to do that.
 */
const defineTableSchema = defs => {
  defs.fieldDefinitions = standardizeDefinitions(defs.fieldDefinitions)

  /**
   * This ensures that any code from this point on does not mutate this data definion object, and because we have
   * nested objects we need to recursively do this.
   */
  return deepFreeze(defs)
}

export default defineTableSchema
export { defaultColumnProperties }
